import type { Block, PresetRef } from "../contract/types";

/**
 * Cliente HTTP del protocolo (docs/protocol.md).
 *
 * Todo lo que devuelve el backend se trata como dato no confiable: se valida
 * la forma mínima y se normaliza antes de entrar al estado de React. Nada de
 * `any`: lo que no encaja simplemente se descarta.
 */

/**
 * Origen del backend.
 *
 * En desarrollo va vacío y las peticiones salen relativas (`/api/...`), que es
 * lo que Vite proxifica a `localhost:8000`. En producción el frontend se sirve
 * desde `tangerine.ferbend.com` y la API vive en otro subdominio, así que hay
 * que apuntarla de forma absoluta en tiempo de build:
 *
 *     VITE_API_ORIGIN=https://api.tangerine.ferbend.com
 *
 * Sin barra final: se le añade `/api` aquí para que ambos entornos compartan
 * las mismas rutas.
 */
const API_ORIGIN = (import.meta.env.VITE_API_ORIGIN ?? "").replace(/\/+$/, "");
const API_BASE = `${API_ORIGIN}/api`;

/**
 * Dónde vive el backend, en texto, para los mensajes de error.
 *
 * No se escribe `localhost:8000` a mano en ningún mensaje: en producción eso
 * señalaría a una dirección que no existe y mandaría a buscar el problema al
 * sitio equivocado. Sin origen configurado son peticiones relativas, así que
 * el backend es sencillamente "este mismo sitio".
 */
export const API_LOCATION = API_ORIGIN
  ? API_ORIGIN.replace(/^https?:\/\//, "")
  : "este mismo sitio";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(message: string, status: number, code = "http_error") {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

export interface SessionCreated {
  session_id: string;
}

export interface RunStarted {
  run_id: string;
  message_id: string;
}

export interface HealthInfo {
  ok: boolean;
  bundle_path?: string;
  provider?: string;
  model?: string;
}

/* -------------------------------------------------------------------------- */
/* Mensajes persistidos                                                       */
/* -------------------------------------------------------------------------- */

export interface TextPart {
  kind: "text";
  text: string;
  phase?: "answer" | "process";
}

export interface BlockPart {
  kind: "block";
  block: Block;
}

export interface UiEventPart {
  kind: "ui_event";
  block_id: string;
  event: string;
  payload: Record<string, unknown>;
}

export interface ToolPart {
  kind: "tool";
  name: string;
  ok: boolean;
  preview: string;
  id?: string;
}

export type MessagePart = TextPart | BlockPart | UiEventPart | ToolPart;

export interface SessionMessage {
  id: string;
  role: "user" | "assistant" | "system";
  created_at?: string;
  parts: MessagePart[];
}

export interface SessionDetail {
  session_id: string;
  messages: SessionMessage[];
}

/* -------------------------------------------------------------------------- */
/* Curso y progreso                                                           */
/* -------------------------------------------------------------------------- */

export interface OutlineModule {
  module: string;
  title: string;
  topics: string[];
  unit?: string;
}

export interface OutlineUnit {
  unit: string;
  title: string;
  modules: OutlineModule[];
}

export interface CourseOutline {
  units: OutlineUnit[];
  evaluation?: RubricData;
}

export interface ProgressModule {
  module: string;
  topics: number;
  quizzes: number;
  accuracy: number;
}

export interface CourseProgress {
  modules: ProgressModule[];
}

export interface RubricData {
  total_label?: string;
  formula?: string;
  components: { code: string; label: string; weight: number; description?: string; modules?: string[] }[];
}

export interface SourceData {
  path: string;
  title?: string;
  kind?: "course" | "outside" | "pdf";
  snippet?: string;
  trust?: "unverified" | "machine-confirmed" | "human-reviewed";
  stale?: boolean;
  page?: number;
}

/* -------------------------------------------------------------------------- */
/* Utilidades internas                                                        */
/* -------------------------------------------------------------------------- */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        Accept: "application/json",
        ...(init?.body ? { "Content-Type": "application/json" } : {}),
        ...init?.headers,
      },
    });
  } catch (cause) {
    throw new ApiError(
      cause instanceof Error ? cause.message : "No se pudo contactar al backend",
      0,
      "network_error",
    );
  }

  if (!response.ok) {
    // 502/503/504 significan lo mismo en los dos entornos: el proxy que hay
    // delante está en pie pero el backend detrás no. Decirlo así ahorra el
    // rato de interpretar el código de estado.
    const gatewayDown = response.status === 502 || response.status === 503 || response.status === 504;
    let message = gatewayDown
      ? `El backend del agente no responde en ${API_LOCATION}`
      : `El backend respondió ${response.status}`;
    let code = gatewayDown ? "backend_unreachable" : "http_error";
    try {
      const body: unknown = await response.json();
      if (isRecord(body)) {
        message = gatewayDown
          ? message
          : (asString(body.message) ?? asString(body.detail) ?? message);
        code = asString(body.code) ?? code;
      }
    } catch {
      /* el cuerpo no era JSON: nos quedamos con el mensaje por defecto */
    }
    throw new ApiError(message, response.status, code);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/* -------------------------------------------------------------------------- */
/* Endpoints                                                                  */
/* -------------------------------------------------------------------------- */

export function getHealth(): Promise<HealthInfo> {
  return request<HealthInfo>("/health");
}

export async function createSession(): Promise<string> {
  const body = await request<unknown>("/sessions", { method: "POST" });
  const id = isRecord(body) ? asString(body.session_id) : null;
  if (!id) throw new ApiError("El backend no devolvió session_id", 500, "bad_payload");
  return id;
}

export async function getSession(sessionId: string): Promise<SessionDetail> {
  const body = await request<unknown>(`/sessions/${encodeURIComponent(sessionId)}`);
  return normalizeSessionDetail(body, sessionId);
}

export async function sendMessage(sessionId: string, content: string): Promise<RunStarted> {
  const body = await request<unknown>(`/sessions/${encodeURIComponent(sessionId)}/messages`, {
    method: "POST",
    body: JSON.stringify({ content }),
  });
  return normalizeRunStarted(body);
}

export async function sendAction(
  sessionId: string,
  action: { block_id: string; event: string; payload: Record<string, unknown> },
): Promise<RunStarted> {
  const body = await request<unknown>(`/sessions/${encodeURIComponent(sessionId)}/actions`, {
    method: "POST",
    body: JSON.stringify(action),
  });
  return normalizeRunStarted(body);
}

export async function getCourseOutline(): Promise<CourseOutline | null> {
  try {
    return normalizeOutline(await request<unknown>("/course/outline"));
  } catch {
    return null;
  }
}

export async function getProgress(): Promise<CourseProgress | null> {
  try {
    return normalizeProgress(await request<unknown>("/progress"));
  } catch {
    return null;
  }
}

export interface SourceIndex {
  /** Los documentos que devolvió esta consulta (recortados por `limit`). */
  sources: SourceData[];
  /**
   * Cuántos conceptos tiene el bundle EN TOTAL, no cuántos se pidieron.
   * Es `null` si el backend no lo informa: en ese caso la UI no debe afirmar
   * ningún total, porque `sources.length` es solo el tamaño de esta página.
   */
  total: number | null;
}

export async function getSources(limit = 40): Promise<SourceIndex> {
  try {
    const body = await request<unknown>(`/sources?limit=${limit}`);
    const list = Array.isArray(body) ? body : isRecord(body) ? asArray(body.sources) : [];
    const total = isRecord(body) ? asNumber(body.total) : null;
    const sources = list
      .map(normalizeSource)
      .filter((source): source is SourceData => source !== null);
    return { sources, total };
  } catch {
    return { sources: [], total: null };
  }
}

/** URL del stream SSE de un run. */
export function runStreamUrl(sessionId: string, runId: string): string {
  return `${API_BASE}/sessions/${encodeURIComponent(sessionId)}/runs/${encodeURIComponent(runId)}/stream`;
}

/* -------------------------------------------------------------------------- */
/* Normalización                                                              */
/* -------------------------------------------------------------------------- */

function normalizeRunStarted(body: unknown): RunStarted {
  if (!isRecord(body)) throw new ApiError("Respuesta inesperada del backend", 500, "bad_payload");
  const runId = asString(body.run_id);
  const messageId = asString(body.message_id) ?? "";
  if (!runId) throw new ApiError("El backend no devolvió run_id", 500, "bad_payload");
  return { run_id: runId, message_id: messageId };
}

/**
 * El contrato define los átomos con sus props en la raíz (`latex`, `cards`…),
 * pero el ejemplo de mensaje persistido del protocolo los envuelve en `props`.
 * Se aceptan las dos formas: si viene `props`, se eleva.
 */
export function normalizeBlock(value: unknown): Block | null {
  if (!isRecord(value)) return null;
  const type = asString(value.type);
  const id = asString(value.id);
  if (!type || !id) return null;
  const { props, ...rest } = value;
  const merged = isRecord(props) ? { ...props, ...rest } : rest;
  return { ...merged, id, type } as Block;
}

/** Un preset puede llegar dentro del envelope de un bloque del stream. */
export function normalizePreset(value: unknown): PresetRef | null {
  if (!isRecord(value)) return null;
  const name = asString(value.name);
  if (!name) return null;
  const known: readonly string[] = [
    "course_overview",
    "unit_dashboard",
    "concept_deep_dive",
    "case_study",
    "assessment_brief",
    "study_path",
  ];
  if (!known.includes(name)) return null;
  return {
    name: name as PresetRef["name"],
    data: isRecord(value.data) ? value.data : {},
    title: asString(value.title),
  };
}

function normalizeParts(value: unknown): MessagePart[] {
  const parts: MessagePart[] = [];
  for (const raw of asArray(value)) {
    if (!isRecord(raw)) continue;
    const kind = asString(raw.kind);
    if (kind === "text") {
      parts.push({ kind: "text", text: asString(raw.text) ?? "", phase: raw.phase === "process" ? "process" : "answer" });
    } else if (kind === "block") {
      const block = normalizeBlock(raw.block);
      if (block) parts.push({ kind: "block", block });
    } else if (kind === "ui_event") {
      parts.push({
        kind: "ui_event",
        block_id: asString(raw.block_id) ?? "",
        event: asString(raw.event) ?? "",
        payload: isRecord(raw.payload) ? raw.payload : {},
      });
    } else if (kind === "tool") {
      parts.push({
        kind: "tool",
        name: asString(raw.name) ?? "tool",
        ok: raw.ok === true,
        preview: asString(raw.preview) ?? "",
        id: asString(raw.id) ?? undefined,
      });
    }
  }
  return parts;
}

export function normalizeSessionDetail(body: unknown, fallbackId: string): SessionDetail {
  if (!isRecord(body)) return { session_id: fallbackId, messages: [] };
  const messages: SessionMessage[] = [];
  for (const raw of asArray(body.messages)) {
    if (!isRecord(raw)) continue;
    const role = asString(raw.role);
    if (role !== "user" && role !== "assistant" && role !== "system") continue;
    messages.push({
      id: asString(raw.id) ?? `msg_${messages.length}`,
      role,
      created_at: asString(raw.created_at) ?? undefined,
      parts: normalizeParts(raw.parts),
    });
  }
  return { session_id: asString(body.session_id) ?? fallbackId, messages };
}

function normalizeSource(value: unknown): SourceData | null {
  if (!isRecord(value)) return null;
  const path = asString(value.path);
  if (!path) return null;
  const kind = asString(value.kind);
  const trust = asString(value.trust);
  return {
    path,
    title: asString(value.title) ?? undefined,
    kind: kind === "course" || kind === "outside" || kind === "pdf" ? kind : undefined,
    snippet: asString(value.snippet) ?? undefined,
    trust:
      trust === "unverified" || trust === "machine-confirmed" || trust === "human-reviewed"
        ? trust
        : undefined,
    stale: typeof value.stale === "boolean" ? value.stale : undefined,
    page: asNumber(value.page) ?? undefined,
  };
}

function normalizeOutline(body: unknown): CourseOutline | null {
  if (!isRecord(body)) return null;
  const units: OutlineUnit[] = [];
  for (const rawUnit of asArray(body.units)) {
    if (!isRecord(rawUnit)) continue;
    const modules: OutlineModule[] = [];
    for (const rawModule of asArray(rawUnit.modules)) {
      if (!isRecord(rawModule)) continue;
      // `code` es el nombre que usa el backend; `module`/`id`/`number` son
      // variantes toleradas. Si ninguna aparece, el módulo colapsa a clave
      // vacía y React duplica keys en toda la lista.
      const id = rawModule.module ?? rawModule.code ?? rawModule.id ?? rawModule.number;
      modules.push({
        module: typeof id === "number" ? String(id) : (asString(id) ?? ""),
        title: asString(rawModule.title) ?? "",
        topics: asArray(rawModule.topics)
          .map((topic) => asString(topic))
          .filter((topic): topic is string => topic !== null),
        unit: asString(rawUnit.unit) ?? asString(rawUnit.code) ?? undefined,
      });
    }
    units.push({
      unit: asString(rawUnit.unit) ?? asString(rawUnit.code) ?? String(units.length + 1),
      title: asString(rawUnit.title) ?? "",
      modules,
    });
  }
  if (units.length === 0) return null;
  return { units, evaluation: normalizeRubric(body.evaluation) };
}

function normalizeRubric(value: unknown): RubricData | undefined {
  if (!isRecord(value)) return undefined;
  const components = asArray(value.components)
    .map((raw) => {
      if (!isRecord(raw)) return null;
      const code = asString(raw.code);
      const label = asString(raw.label);
      const weight = asNumber(raw.weight);
      if (!code || !label || weight === null) return null;
      return {
        code,
        label,
        weight,
        description: asString(raw.description) ?? undefined,
        modules: asArray(raw.modules)
          .map((module) => asString(module))
          .filter((module): module is string => module !== null),
      };
    })
    .filter((component): component is NonNullable<typeof component> => component !== null);
  if (components.length === 0) return undefined;
  return {
    total_label: asString(value.total_label) ?? undefined,
    formula: asString(value.formula) ?? undefined,
    components,
  };
}

function normalizeProgress(body: unknown): CourseProgress | null {
  if (!isRecord(body)) return null;
  const modules: ProgressModule[] = [];
  for (const raw of asArray(body.modules)) {
    if (!isRecord(raw)) continue;
    const id = raw.module;
    modules.push({
      module: typeof id === "number" ? String(id) : (asString(id) ?? ""),
      topics: asNumber(raw.topics) ?? 0,
      quizzes: asNumber(raw.quizzes) ?? 0,
      accuracy: asNumber(raw.accuracy) ?? 0,
    });
  }
  if (modules.length === 0) return null;
  return { modules };
}
